//
// # Wire Cleanup Command
//
// Tidy every wire in a schematic: re-route each run between terminals into a
// minimum-bend path, then collapse whatever collinear geometry that leaves.
//
// Built on the same pieces a drag uses — `planCleanupEdits` shares the move
// planner's decomposition, and the edits are applied and merged by the same
// code. This was previously a parallel implementation with its own network
// tracer, its own routing and its own change logging, which is exactly the
// condition that let the two disagree about what is connected to what.
//

import { planCleanupEdits } from "DrawCore/src/routing/wire_plan";
import { pointKey, getSchPinWorldLocation } from "DrawCore/src/routing";
import * as changes from "DrawCore/src/changes";
import type { Schematic } from "../content/schematic/schematic";
import { applyWireEdits, mergeOverlaps, snapshotWires } from "./wire_edits";

// Result of a cleanup operation
export interface CleanupResult {
  networksProcessed: number;
  changesApplied: number;
}

export function cleanupWires(schematic: Schematic): CleanupResult {
  const targets = snapshotWires(schematic.render);
  if (targets.segments.length === 0) {
    return { networksProcessed: 0, changesApplied: 0 };
  }

  const pinKeys = new Set<string>();
  for (const instance of schematic.instances) {
    for (const loc of instance.getPinWorldLocations()) {
      pinKeys.add(pointKey(loc));
    }
  }
  for (const schPin of schematic.pins) {
    pinKeys.add(pointKey(getSchPinWorldLocation(schPin)));
  }

  const { edits, runs } = planCleanupEdits({
    wires: targets.segments.map((seg) => ({
      start: seg.start(),
      end: seg.end(),
    })),
    pinKeys,
    // A detour around a pin invents its crossover coordinate; snap it to the
    // editor's grid, as a drag does.
    snap: schematic.editor.grid.nearestPoint,
    // No obstacles. Every run here ends on a pin, so the instances owning
    // those pins would have to be excluded per run — as the drag path does —
    // and passing the rest without that exclusion just scores routes against
    // boxes they are required to reach. Tidying existing geometry does not
    // need it; add it properly if cleanup should start avoiding instances.
  });

  const singleChanges = applyWireEdits(schematic.render, targets, edits);
  singleChanges.push(...mergeOverlaps(schematic.render));

  if (singleChanges.length === 1) {
    schematic.logChange(singleChanges[0]);
  } else if (singleChanges.length > 1) {
    const combo = changes.Combo.fromArray(singleChanges);
    if (combo.ok) schematic.logChange(combo.val);
  }

  schematic.updateDots();
  return { networksProcessed: runs, changesApplied: singleChanges.length };
}
